import { request, CustomException } from '@tencent/ppd-base-libs';
import { to } from '@tencent/wphk-base-utils';
import { getServerTime } from '@tencent/wphk-components-common/utils/server-time';
import dayjs from 'dayjs';
import IsSameOrBefore from 'dayjs/plugin/isSameOrBefore.js';
import { qryUserQual, qryUserWepoints } from '@tencent/wphk-act-base-cgi';

dayjs.extend(IsSameOrBefore);

/** 积分ID */
const WE_POINTS_PRIZE_ID = 'VOkjufHAfSekWqSXmroI/Q==';

/**
 * 获取电子优惠券数量
 */
export async function getECouponCount(): Promise<number> {
  const [err, res] = await to(request.get('/app/promotion/prom_qry_usercoup_cnt.cgi'));
  return err ? 0 : parseInt(res.total_cnt || 0, 10);
}

/**
 * 获取商户优惠券数量
 */
export async function getSpCouponCount(): Promise<number> {
  const [err, res] = await to(request.get('/app/sp_coupon/prom_qry_sp_coupon_cnt.cgi'));
  return err ? 0 : parseInt(res.total_cnt || 0, 10);
}

/**
 * 获取我的优惠券数量
 */
export async function getMyCouponCount(): Promise<number> {
  const res = await Promise.all([getECouponCount(), getSpCouponCount()]);
  return res[0] + res[1];
}

/**
 * 兼容配置系统： 把数组的第一项作为对象返回
 */
const getArrayFirstItemAsObj = (arr: any[]) => arr?.[0] || {};

/**
 * 校验某时间是否在开始和结束时间之间,闭区间（不使用mock时间）
 */
export function checkTimeBetween({ targetTime, startDate, endDate }: {
  startDate: Date | string,
  endDate: Date | string,
  targetTime: Date | string
}): boolean {
  if (!targetTime || !startDate || !endDate) {
    throw new Error('[checkTimeBetween]: params missing');
  }
  return dayjs(startDate).isSameOrBefore(dayjs(targetTime)) && dayjs(targetTime).isSameOrBefore(dayjs(endDate));
}

const getConfigByPeriod = (configArr: any[]) => {
  const serverTime = getServerTime();
  const targetConfig = configArr.find((item) => {
    const { startTime, endTime } = getArrayFirstItemAsObj(item.periodConfig);

    if (!startTime || !endTime || !serverTime) {
      throw new CustomException({ startTime, endTime, serverTime }, 'eStampConfigTimeEmpty', '新版狂赏印时间配置异常');
    }
    // 转成Date格式
    const startDate = new Date(dayjs(startTime).format('YYYY/MM/DD HH:mm:ss'));
    const endDate = new Date(dayjs(endTime).format('YYYY/MM/DD HH:mm:ss'));
    // 校验时间，返回当前时间区间的配置
    return checkTimeBetween({ targetTime: serverTime, startDate, endDate });
  });
  if (!targetConfig) {
    throw new CustomException({ serverTime }, 'eStampConfigNotFound', '新版狂赏印配置为空');
  }
  return targetConfig;
};

/**
 * 获取印花配置
 * @returns
 */
export const getEStampConfig = async () => {
  const url = `https://st.moneydata.hk/res/cc/eStampAllConfig/config.json?v=${new Date().getTime()}`;
  const res = await request.loadVtools(url);
  const config = getConfigByPeriod(res.configList);
  return config.commonConfig;
};

/**
 * 是否在维护期间
 */
const isInBlockPeriod = function (blockDialogConfig = {}) {
  const { startTime, endTime } = blockDialogConfig as any;
  if (!startTime || !endTime) {
    return false;
  }
  const isInPeriod = checkTimeBetween({
    targetTime: getServerTime(),
    startDate: startTime,
    endDate: endTime,
  });
  return isInPeriod;
};

/**
 * 获取我的印花数量
 */
export const getMyEStampCount = async () => {
  const config = await getEStampConfig();
  const { stampConfig, blockDialogConfig } = config;
  if (isInBlockPeriod(blockDialogConfig[0])) {
    return 0;
  }
  const data = await qryUserQual({ qualId: decodeURIComponent(stampConfig[0].stampId) });
  return data.totalCnt - data.usedCnt;
};

/**
 * 获取用户积分数量
 */
export const getMyWePointsCount = async () => {
  const { balance, freezeBalance } = await qryUserWepoints({
    qualId: WE_POINTS_PRIZE_ID,
  });
  return balance - freezeBalance;
};
